import { dataLayer } from 'analytics/data-layer';
import extractLineNumberFromSku from 'utils/extract-linenumber-from-sku';
import { GTM_TRACKED_CONFLICTS, AVAILABLE, UNAVAILABLE } from 'constants/conflicts';
import { CampaignName, SlotGridType } from 'api/definitions/slotdates/types';
import { formatGroceriesMenuNavigation } from 'utils/format';
import { getProductFindingMethod } from 'utils/get-product-finding-method';
import { getFeatureFlags } from 'utils/feature-flags';
import { getItemListFormatted, mapToImpression } from 'analytics/product-impressions-tracking';
import { getPricingAndOfferDetails } from 'hooks/use-pod-impression';

// TODO: Migrate all product to impression mapping to a shared selector utility
// REF: https://johnlewispartnership.atlassian.net/browse/SHOPEXP-90
export function handleProductListClick({
  conflictMessage = '',
  isFavourite,
  productData,
  position,
  boosted,
  sponsored,
  sponsorshipId = undefined,
  productFindingMethod: productFindingMethodCarousel = undefined,
  listDefinitions,
  orderId = undefined,
  promotions,
  podType,
  purchaseOrAmend = 'purchase',
}) {
  const productFindingMethod = productFindingMethodCarousel || getProductFindingMethod();

  let availability = AVAILABLE;

  if (conflictMessage) {
    availability = GTM_TRACKED_CONFLICTS.includes(conflictMessage) ? conflictMessage : UNAVAILABLE;
  }

  const { currencyCode, discount, offer, offerId, price, uom } = getPricingAndOfferDetails(
    productData,
    promotions,
  );

  const lineNumber = extractLineNumberFromSku(productData.id) || '';
  const categoriesNames = (productData.categories || []).map(category => category.name);
  const favourite = isFavourite ? `YES${boosted ? ': Boosted' : ''}` : 'NO';

  const impression = mapToImpression({
    availability,
    brand: productData.brand,
    position,
    currencyCode,
    id: productData.id,
    podType,
    lineNumber,
    name: productData.name || '',
    offer: offer ?? productData.promotion?.promotionDescription ?? '',
    price,
    quantity: 1,
    listDefinitions,
    productFindingMethod,
    sponsored,
    sponsorshipId,
    uom: uom || '',
    categoriesNames,
    discount,
    offerId,
    productType: productData.productType,
    isFavourite: !!isFavourite,
  });

  // Override favourite with boosted suffix if applicable
  impression.favourite = favourite;

  const itemListFormatted = getItemListFormatted(listDefinitions, productFindingMethod);

  dataLayer.push({
    event: 'select_item',
    ecommerce: {
      ...itemListFormatted,
      currencyCode: currencyCode || 'GBP',
      order_id: orderId,
      purchase_or_amend: purchaseOrAmend,
      value: price || 0,
      items: [impression],
    },
  });
}
export function pushSlotResultsToGTMDataLayer({
  postcode,
  branchId,
  availableSlots,
  fulfilmentType,
  maxCharge,
  minCharge,
  customerId,
  greenSlotsCount,
  greenSlotVariant,
  isDeliveryPassActive,
}) {
  const postcodeTrimmed = postcode?.slice(0, -3).trim();

  const slotResultsObj = {
    event: 'book_slot_results',
    ...(isDeliveryPassActive && { delivery_pass_status: 'active' }),
    book_slot: {
      type: fulfilmentType,
      results: {
        number_of_available_slots: availableSlots,
        number_of_available_green_slots: greenSlotsCount,
        max_charge: maxCharge,
        min_charge: minCharge,
        customer_id: customerId,
        variant_type: greenSlotVariant,
      },
    },
    user: {
      branch_id: branchId,
      postcode: postcodeTrimmed,
    },
  };
  dataLayer.push(slotResultsObj);
}

export function pushSlotBookedToGTMDataLayer(
  postcode,
  branchId,
  earliestDate,
  time,
  bookSlotType,
  slotCharge,
  orderId,
  date,
  slotDuration,
  customerId,
  greenSlotVariant,
  isDeliveryPassActive,
) {
  const postcodeTrimmed = postcode.slice(0, -3).trim();

  const bookedSlotObj = {
    event: 'book_slot_booked',
    ...(isDeliveryPassActive && { delivery_pass_status: 'active' }),
    book_slot: {
      type: bookSlotType,
      slot_charge: slotCharge,
      customer_id: customerId,
      variant_type: greenSlotVariant,
      booked: {
        earliest_date: earliestDate,
        date,
        time,
      },
      slot_booked_successful: 'YES',
      slot_duration: slotDuration,
    },
    user: {
      branch_id: branchId,
      postcode: postcodeTrimmed,
    },
    order_id: orderId,
  };
  dataLayer.push(bookedSlotObj);
}

export function pushFormErrorToGTMDataLayer(form, error) {
  dataLayer.push({
    event: 'form_error',
    form,
    error,
  });
}

export function pushRegistrationToGTMDataLayer(
  defaultMarketingConsents,
  marketingConsents,
  customerId,
) {
  dataLayer.push({
    event: 'registration_complete',
    default_marketing_consents: defaultMarketingConsents,
    marketingConsents,
    user: { customer_id: customerId },
  });
}

export function pushEnterCheckoutEvent(location) {
  dataLayer.push({
    event: 'enter_checkout',
    location,
  });
}

export function pushStartCheckoutEvent(isAmendOrder, products, canInstantCheckout) {
  const optionField = isAmendOrder ? 'amend' : 'purchase';

  dataLayer.push({
    event: 'checkout_start',
    instant_checkout_eligibility: canInstantCheckout,
    ecommerce: {
      checkout: {
        actionField: {
          option: optionField,
        },
        products,
      },
    },
  });
}

const getCustomerType = customerHasACompletedOrder => {
  if (customerHasACompletedOrder === undefined) {
    return undefined;
  }

  return customerHasACompletedOrder ? 'Existing' : 'New';
};

export function generateCustomerEventForGTMDataLayer(
  customerId,
  isMyWaitroseCustomer,
  customerHasACompletedOrder,
  eventName = null,
) {
  const customerEvent = {
    event: eventName,
    user: {
      customer_id: customerId,
      customer_type: getCustomerType(customerHasACompletedOrder),
      mywaitrose_customer: isMyWaitroseCustomer,
    },
  };

  if (customerEvent.event === null || customerEvent.event === undefined) {
    delete customerEvent.event;
  }

  return customerEvent;
}

export function pushMenuClick(navigation, additionalData = {}) {
  dataLayer.push({
    event: 'click_megamenu',
    mega_menu: {
      button_clicked: navigation,
      ...additionalData,
    },
  });
}

/**
 * @param {object} params
 */
export function pushMenuClickWithLevels(
  { level, name, menus, additionalData } = { additionalData: {} },
) {
  const navigation =
    level > 0
      ? `${formatGroceriesMenuNavigation(menus.flatMap(item => item.name))}|G${level + 1}:${name}`
      : `${name}`;

  pushMenuClick(navigation, additionalData);
}

export function pushSlotGridNavigationEventToGTMDataLayer(isLater) {
  const method = isLater ? 'Later Button' : 'Earlier Button';
  const slotGridNavigationEvent = {
    event: 'slot_grid_navigation',
    navigation: {
      method,
    },
  };

  dataLayer.push(slotGridNavigationEvent);
}

export const SUBS_NOTES_EVENT = {
  decline: 'product_pod_decline_substitution',
  addNote: 'product_pod_add_note_clicked',
  saveNote: 'product_pod_save_note',
};

export function trackSubsNotesClick(event) {
  if (Object.values(SUBS_NOTES_EVENT).includes(event)) {
    dataLayer.push({ event });
  }
}

export function pushAddToListClick() {
  dataLayer.push({
    event: 'click_add_to_list',
  });
}

export function pushInterstitialsStatus(status, orderId) {
  const { EoS_interstitialsAnalytics: eosInterstitialsAnalytics } = getFeatureFlags();
  if (eosInterstitialsAnalytics) return;
  dataLayer.push({
    event: 'interstitials_journey',
    order_id: orderId,
    status,
  });
}

export function pushIsSaverSlotBranchToGTMDataLayer(isSaverSlotBranch) {
  const saverSlotShown = {
    event: 'saver_slot_shown',
    saver_slot_shown: isSaverSlotBranch ? 'YES' : 'NO',
  };

  dataLayer.push(saverSlotShown);
}

export function pushSlotGridChangeToGTMDataLayer(slotGrid) {
  const selectorStateMap = {
    [SlotGridType.DEFAULT_GRID]: 'Slots from today',
    [SlotGridType.SAVER_GRID]: 'Saver slots',
    [CampaignName.CHRISTMAS]: 'Christmas slots',
    [CampaignName.EASTER]: 'Easter slots',
  };

  const slotGridChange = {
    event: 'navigate_book_slot_selector',
    selector_state: selectorStateMap[slotGrid],
  };

  dataLayer.push(slotGridChange);
}

export function pushAddressDeliveryAvailability(status) {
  dataLayer.push({
    event: 'address_delivery_availability',
    delivery_availability_status: status,
  });
}
